"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearIdentity = exports.storeIdentity = exports.getIdentity = exports.init = void 0;
const isURL_1 = require("validator/lib/isURL");
const SingleSignOn_shared_1 = require("./SingleSignOn.shared");
// The id used to identify the iframe.
const IFRAME_ID = SingleSignOn_shared_1.SINGLE_SIGN_ON_TARGET;
// The timeout in milliseconds to wait for the response of the iframe.
// This is used to avoid waiting indefinitely for a response.
const GET_IFRAME_TIMEOUT = 500;
// The number of times to retry getting the iframe.
// After this number of retries, an error is thrown.
const GET_IFRAME_RETRIES = 5;
// Used as the id to identify messages.
// It is incremented every time a message is sent.
let _counter = 0;
// The url of the iframe.
// Being an empty string or not indicates if the iframe has been initialized with the `init` function.
let _src = "";
const defaultOptions = {
    protocols: ["https"],
    require_protocol: true,
};
// Initializes the client by appending the SSO iframe to the document.
function init(src, options = {}) {
    if (!(0, isURL_1.default)(src, { ...defaultOptions, ...options })) {
        throw new Error(`Invalid url: ${src}`);
    }
    if (_src) {
        throw new Error("Already initialized");
    }
    const iframe = document.createElement("iframe");
    iframe.id = IFRAME_ID;
    iframe.src = src;
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.style.position = "absolute";
    document.body.appendChild(iframe);
    _src = src;
}
exports.init = init;
// Gets the identity of the given user from the iframe.
// Fallbacks to the current application's local storage if communication with the iframe fails.
async function getIdentity(user) {
    let identity;
    try {
        const message = await postMessage(await getIframe(), SingleSignOn_shared_1.Action.GET, { user });
        identity = message.identity ?? null;
    }
    catch (e) {
        logFallback(e);
        identity = (0, SingleSignOn_shared_1.localStorageGetIdentity)(user);
    }
    return identity;
}
exports.getIdentity = getIdentity;
// Stores the identity of the given user into the iframe.
// Fallbacks to the current application's local storage if communication with the iframe fails.
async function storeIdentity(user, identity) {
    try {
        await postMessage(await getIframe(), SingleSignOn_shared_1.Action.STORE, { user, identity });
    }
    catch (e) {
        logFallback(e);
        (0, SingleSignOn_shared_1.localStorageStoreIdentity)(user, identity);
    }
}
exports.storeIdentity = storeIdentity;
// Clears the identity of the given user from the iframe.
// Fallbacks to the current application's local storage if communication with the iframe fails.
async function clearIdentity(user) {
    try {
        await postMessage(await getIframe(), SingleSignOn_shared_1.Action.CLEAR, { user });
    }
    catch (e) {
        logFallback(e);
        (0, SingleSignOn_shared_1.localStorageClearIdentity)(user);
    }
}
exports.clearIdentity = clearIdentity;
// Gets the iframe content window used to communicate with the iframe through postMessage.
// Has a retry mechanism to wait for the iframe to be ready.
// This has been implemented because some time might pass between the iframe being created and the iframe being ready to communicate.
async function getIframe() {
    if (!_src) {
        throw new Error("Not initialized");
    }
    for (let i = 0; i < GET_IFRAME_RETRIES; i++) {
        const element = document.getElementById(IFRAME_ID);
        const contentWindow = element?.contentWindow;
        if (!contentWindow) {
            await wait(GET_IFRAME_TIMEOUT);
            continue;
        }
        try {
            await postMessage(contentWindow, SingleSignOn_shared_1.Action.PING, {}, GET_IFRAME_TIMEOUT);
        }
        catch (e) {
            continue;
        }
        return contentWindow;
    }
    throw new Error("Could not get iframe because it is not ready or cannot be communicated with");
}
// Simulates a request-response communication with the iframe.
// Sends a message to the iframe with a distinguishable id and waits for the response with the same id.
async function postMessage(iframe, action, payload, timeout = 0) {
    _counter++;
    const id = _counter;
    let handler = null;
    const request = new Promise((resolve, reject) => {
        handler = ({ data }) => {
            if (!data || data.target !== SingleSignOn_shared_1.SINGLE_SIGN_ON_TARGET || data.id !== id) {
                return;
            }
            if (data.error) {
                reject(new Error(data.error));
            }
            else {
                resolve(data);
            }
        };
        window.addEventListener("message", handler);
    });
    iframe.postMessage((0, SingleSignOn_shared_1.createMessage)({ id, action, ...payload }), _src);
    try {
        return await (timeout
            ? Promise.race([
                request,
                new Promise((_, reject) => setTimeout(() => reject(new Error("Did not receive a response in time")), timeout)),
            ])
            : request);
    }
    catch (error) {
        throw error;
    }
    finally {
        if (handler) {
            window.removeEventListener("message", handler);
        }
    }
}
// Helper function to wait for a given amount of time in milliseconds.
function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
// Helper function to log a warning when the iframe cannot be communicated with.
function logFallback(error) {
    console.warn("Could not get identity from iframe, falling back to localStorage. Error:", error.message);
}
//# sourceMappingURL=SingleSignOn.js.map